import { forwardRef, type CSSProperties, type JSX } from "react";
import type { ImpactPlusReport, ImpactPlusCohort } from "../../entities/report/schemas/ImpactPlusReport.schema";
import type { UseCase } from "../../entities/useCaseChat/UseCase";
import { useReportPagination } from "@/hooks/useReportPagination";
import { useTocPageNumbers } from "@/hooks/useTocPageNumbers";
import reportCoverBg from "../../assets/impact-new-bg.png";
import aiqurisCoverLogo from "../../assets/AIQURIS Logo Horizontal White and Light Blue (1).png";
import aiqurisLogo from "../../assets/Aiquris Logo.png";
import impactPlusWordmark from "../../assets/impact-white.png";

const MIcon = ({ name, size = 18 }: { name: string; size?: number }) => (
  <span className="material-icon" style={{ fontSize: size }} aria-hidden="true">
    {name}
  </span>
);

const VerdictIconGppMaybe = () => <MIcon name="gpp_maybe" size={28} />;
const ShieldOutlineIcon = () => <MIcon name="shield" size={17} />;
const ChecklistIcon = () => <MIcon name="fact_check" size={17} />;
const WarningTriangleIcon = () => <MIcon name="warning" size={17} />;
const GroupIcon = () => <MIcon name="group" size={19} />;
const StorageIcon = () => <MIcon name="database" size={19} />;
const GlobeIcon = () => <MIcon name="public" size={17} />;
const DomainIcon = () => <MIcon name="domain" size={19} />;
const InfoIcon = () => <MIcon name="info" size={17} />;
const ArrowCircleRightIcon = () => <MIcon name="arrow_circle_right" size={28} />;
const MonitoringIcon = () => <MIcon name="monitoring" size={17} />;

const FALLBACK_CELL_VALUE = "-";

const STAKEHOLDER_RANK_PILL_STYLE: Record<string, { fg: string; bg: string }> = {
  critical: { fg: "var(--critical)", bg: "#FDF0EF" },
  major: { fg: "var(--high)", bg: "#FEF0E7" },
  moderate: { fg: "var(--moderate)", bg: "#FFF8E1" },
  minor: { fg: "var(--low)", bg: "#EBF7EE" },
  negligible: { fg: "var(--risk-negligible)", bg: "#F3F4F6" },
};

const STAKEHOLDER_RANK_CARD_LABEL: Record<string, string> = {
  critical: "CRITICAL",
  major: "MAJOR",
  moderate: "MODERATE",
  minor: "MINOR",
  negligible: "NEGLIGIBLE",
};

const STAKEHOLDER_RANK_TITLE: Record<string, string> = {
  critical: "Critical",
  major: "Major",
  moderate: "Moderate",
  minor: "Minor",
  negligible: "Negligible",
};

const STAKEHOLDER_RANK_FILL_TEXT: Record<string, string> = {
  critical: "var(--white)",
  major: "var(--white)",
  moderate: "var(--assured-dark-blue)",
  minor: "var(--white)",
  negligible: "var(--white)",
};

const getDisplayValue = (value: unknown) => {
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized.toUpperCase() === "N/A") return FALLBACK_CELL_VALUE;
  return normalized;
};

const ReportFooterMainPage = () => (
  <div className="report-footer report-footer-main-page">
    <div className="footer-left"></div>
    <div className="footer-center">© {new Date().getFullYear()} AIQURIS. Confidential and Proprietary.</div>
    <div className="footer-right">
      <img src={aiqurisCoverLogo} alt="AIQURIS" className="footer-logo footer-logo-cover p-1" />
    </div>
  </div>
);

const ReportFooterCoverPage = () => (
  <div className="report-footer report-footer-main-page">
    <div className="footer-left"></div>
    <div className="footer-center">© {new Date().getFullYear()} AIQURIS. Confidential and Proprietary.</div>
    <div className="footer-right"></div>
  </div>
);

const ReportFooter = () => (
  <div className="report-footer">
    <div className="footer-left"></div>
    <div className="footer-center">© {new Date().getFullYear()} AIQURIS. Confidential and Proprietary.</div>
    <div className="footer-right">
      <img src={aiqurisLogo} alt="AIQURIS" className="footer-logo" />
    </div>
  </div>
);

const REPORT_COVER_TEXT = {
  labelAiSystemAssessed: "AI System Assessed:",
  labelOrganisationName: "Organisation:",
  labelReportDate: "Report Date:",
};

const renderTitlePage = (reportData: ImpactPlusReport) => {
  return (
    <header
      className="container header report-cover"
      style={{ backgroundImage: `url(${reportCoverBg})` }}
    >
      <img src={aiqurisCoverLogo} alt="AIQURIS" className="report-cover-top-logo" />
      <div className="report-cover-inner">
        <div className="impact-cover-wordmark">
          <img src={impactPlusWordmark} alt="Impact+" className="impact-cover-wordmark-logo" />
          <span className="impact-cover-wordmark-suffix">Report</span>
        </div>
        <div className="meta impact-cover-meta">
          <div className="impact-cover-meta-col">
            <span className="impact-cover-meta-label">{REPORT_COVER_TEXT.labelAiSystemAssessed.replace(":", "").toUpperCase()}</span>
            <span className="impact-cover-meta-value">{getDisplayValue(reportData.titlePage.useCaseTitle)}</span>
          </div>
          <div className="impact-cover-meta-divider" />
          <div className="impact-cover-meta-col">
            <span className="impact-cover-meta-label">{REPORT_COVER_TEXT.labelOrganisationName.replace(":", "").toUpperCase()}</span>
            <span className="impact-cover-meta-value">{getDisplayValue(reportData.titlePage.client)}</span>
          </div>
          {reportData.titlePage.reportDate && (
            <>
              <div className="impact-cover-meta-divider" />
              <div className="impact-cover-meta-col">
                <span className="impact-cover-meta-label">{REPORT_COVER_TEXT.labelReportDate.replace(":", "").toUpperCase()}</span>
                <span className="impact-cover-meta-value">
                  {new Date(reportData.titlePage.reportDate).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      <ReportFooterCoverPage />
    </header>
  );
};

type StakeholderHarmRank = "critical" | "major" | "moderate" | "minor" | "negligible";

type StakeholderImpactRow = {
  stakeholderGroup: string;
  stakeholderDescription: string;
  criticalAndMajorHarms: string[];
  harmDomain: string[];
  detailAndReasoning: string;
  harmRank: StakeholderHarmRank;
  harmRankLabel?: string;
};

const cohortRiskToHarmRank = (risk: string): StakeholderHarmRank => {
  switch (risk.toLowerCase().trim()) {
    case "critical": return "critical";
    case "high": return "major";
    case "moderate": return "moderate";
    case "low": return "minor";
    default: return "negligible";
  }
};


const cohortToStakeholderRow = (cohort: ImpactPlusCohort): StakeholderImpactRow => {
  const harmRank = cohortRiskToHarmRank(cohort.cohortRisk);

  const significantHarms = cohort.harms
    .map((h) => h.description)
    .filter(Boolean);

  const harmDescriptions =
    significantHarms.length > 0
      ? significantHarms
      : cohort.harms.map((h) => h.description).filter(Boolean);

  const categoryLabel =
    cohort.stakeholderCategory === "people"
      ? "People"
      : cohort.stakeholderCategory === "organisation"
        ? "Organisation"
        : "Ecosystem";

  return {
    stakeholderGroup: cohort.label || FALLBACK_CELL_VALUE,
    stakeholderDescription: cohort.description || FALLBACK_CELL_VALUE,
    criticalAndMajorHarms: harmDescriptions.length > 0 ? harmDescriptions : [FALLBACK_CELL_VALUE],
    harmDomain: [categoryLabel],
    detailAndReasoning: cohort.harmSummary || cohort.harmJustification || FALLBACK_CELL_VALUE,
    harmRank,
    harmRankLabel: undefined,
  };
};

const HARM_RANK_ORDER: Record<StakeholderHarmRank, number> = {
  critical: 0,
  major: 1,
  moderate: 2,
  minor: 3,
  negligible: 4,
};

const STAKEHOLDER_DIMENSIONS: {
  category: ImpactPlusCohort["stakeholderCategory"];
  label: string;
  description: string;
  icon: () => JSX.Element;
}[] = [
    { category: "people", label: "People", description: "Harms borne directly by individuals", icon: () => <MIcon name="diversity_3" size={20} /> },
    { category: "organisation", label: "Organisation", description: "Harms to the deploying and connected organisations", icon: () => <MIcon name="apartment" size={20} /> },
    { category: "ecosystem", label: "Ecosystem", description: "Harms across the wider system and society", icon: () => <MIcon name="travel_explore" size={20} /> },
  ];

const RANK_LEGEND: StakeholderHarmRank[] = ["critical", "major", "moderate", "minor", "negligible"];

const rankPill = (rank: StakeholderHarmRank) => (
  <span className="stakeholder-rank-pill">
    <span
      className="stakeholder-rank-pill-dot"
      aria-hidden="true"
      style={{ backgroundColor: STAKEHOLDER_RANK_PILL_STYLE[rank]?.fg }}
    />
    <span className="stakeholder-rank-pill-label">{STAKEHOLDER_RANK_TITLE[rank] ?? rank}</span>
  </span>
);

const countUndetailedCohorts = (reportData: ImpactPlusReport, detailedCount: number): number => {
  const rankedTotal = reportData.decisionGuidance.cohortRankings.reduce((sum, r) => sum + (r.numCohorts ?? 0), 0);
  return Math.max(0, rankedTotal - detailedCount);
};

const renderStakeholderMapSection = (reportData: ImpactPlusReport) => {
  const cohorts = reportData.impactedStakeholders?.impactedCohorts ?? [];
  if (cohorts.length === 0) return null;

  const totalAssessed = cohorts.length + countUndetailedCohorts(reportData, cohorts.length);
  const undetailed = countUndetailedCohorts(reportData, cohorts.length);

  const groups = STAKEHOLDER_DIMENSIONS.map((dim) => ({
    ...dim,
    cohorts: cohorts
      .filter((c) => c.stakeholderCategory === dim.category)
      .map(cohortToStakeholderRow)
      .sort((a, b) => HARM_RANK_ORDER[a.harmRank] - HARM_RANK_ORDER[b.harmRank]),
  })).filter((g) => g.cohorts.length > 0);

  return (
    <section className="container report-page stakeholder-map-page" id="toc-stakeholder-impact-assessment">
      <div className="usecase-register-eyebrow">03&nbsp;&nbsp;STAKEHOLDER IMPACT ASSESSMENT</div>
      <h1 className="usecase-register-title">Who bears the harm</h1>
      <p className="usecase-register-summary">
        {totalAssessed} cohorts were identified and assessed for reasonably foreseeable impact. They are clustered below
        across the three harm dimensions used throughout this report: People, Organisation, and Ecosystem. Each cohort is
        coloured to its harm rank.
      </p>

      <div className="stakeholder-map-legend">
        <span className="stakeholder-map-legend-caption">Harm rank</span>
        {RANK_LEGEND.map((rank) => (
          <span key={rank} className="stakeholder-map-legend-item">
            <span
              className="stakeholder-map-legend-dot"
              aria-hidden="true"
              style={{ backgroundColor: STAKEHOLDER_RANK_PILL_STYLE[rank]?.fg }}
            />
            {STAKEHOLDER_RANK_TITLE[rank]}
          </span>
        ))}
      </div>

      <div className="stakeholder-map-grid">
        {groups.map((group) => {
          const DimIcon = group.icon;
          return (
            <div key={group.category} className="stakeholder-map-col">
              <div className="stakeholder-map-col-head">
                <div className="stakeholder-map-col-head-row">
                  <span className="stakeholder-map-col-icon" aria-hidden="true"><DimIcon /></span>
                  <span className="stakeholder-map-col-title">{group.label}</span>
                </div>
                <span className="stakeholder-map-col-desc">{group.description}</span>
              </div>
              <div className="stakeholder-map-col-body">
                {group.cohorts.map((row, idx) => (
                  <div key={`${row.stakeholderGroup}-${idx}`} className="stakeholder-map-cohort">
                    <div className="stakeholder-map-cohort-main">
                      <div className="stakeholder-map-cohort-name">{row.stakeholderGroup}</div>
                      {rankPill(row.harmRank)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="stakeholder-map-note">
        <span className="stakeholder-map-note-icon" aria-hidden="true"><InfoIcon /></span>
        <span>
          {cohorts.length} cohort{cohorts.length !== 1 ? "s are" : " is"} detailed on the following pages.
          {undetailed > 0 && (
            <> {undetailed} further cohort{undetailed !== 1 ? "s were" : " was"} assessed at lower impact and do
              {undetailed === 1 ? "es" : ""} not require an individual write-up.</>
          )}{" "}
          The detailed impact cards cover Critical and Major findings in full.
        </span>
      </div>

      <ReportFooter />
    </section>
  );
};

const COHORT_CARDS_PER_PAGE = 4;

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};

const cohortChunkTitle = (rows: StakeholderImpactRow[]): string => {
  const labels = [...new Set(rows.map((r) => (STAKEHOLDER_RANK_TITLE[r.harmRank] ?? r.harmRank).toLowerCase()))];
  const joined =
    labels.length > 1 ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}` : labels[0] ?? "";
  return titleCaseFirst(`${joined} cohorts`);
};

const renderStakeholderCardsSections = (reportData: ImpactPlusReport) => {
  const allRows: StakeholderImpactRow[] = (reportData.impactedStakeholders?.impactedCohorts ?? [])
    .map(cohortToStakeholderRow)
    .sort((a, b) => HARM_RANK_ORDER[a.harmRank] - HARM_RANK_ORDER[b.harmRank]);

  if (allRows.length === 0) return null;

  return chunkArray(allRows, COHORT_CARDS_PER_PAGE).map((rows, pageIdx) => (
    <section
      key={pageIdx}
      className="container report-page stakeholder-cards-page"
      id={pageIdx === 0 ? "toc-stakeholder-cohorts" : undefined}
    >
      <div className="usecase-register-eyebrow">03&nbsp;&nbsp;STAKEHOLDER IMPACT ASSESSMENT</div>
      <h1 className="usecase-register-title">{cohortChunkTitle(rows)}</h1>

      <div className="stakeholder-cohort-list">
        {rows.map((row, idx) => {
          return (
            <div key={`${row.stakeholderGroup}-${idx}`} className="stakeholder-cohort-card">
              <div className="stakeholder-cohort-card-body">
                <div className="stakeholder-cohort-card-head">
                  <div>
                    <div className="stakeholder-cohort-card-name">{row.stakeholderGroup}</div>
                    <div className="stakeholder-cohort-card-dim">{row.harmDomain[0]}</div>
                  </div>
                  {rankPill(row.harmRank)}
                </div>
                <p className="stakeholder-cohort-card-harm">
                  {row.detailAndReasoning !== FALLBACK_CELL_VALUE
                    ? row.detailAndReasoning
                    : row.criticalAndMajorHarms[0] ?? FALLBACK_CELL_VALUE}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <ReportFooter />
    </section>
  ));
};

type HarmLadderRow = {
  n: number;
  rank: StakeholderHarmRank;
  people: string;
  organisation: string;
  ecosystem: string;
};

const HARM_LADDER: HarmLadderRow[] = [
  {
    n: 5,
    rank: "critical",
    people: "Survival threats, life-threatening health crises, forced displacement, acute psychiatric crisis.",
    organisation: "Bankruptcy, regulator shutdown, irrecoverable data breach, incapacity to operate.",
    ecosystem: "Systemic collapse, breakdown of critical infrastructure, inability to sustain members.",
  },
  {
    n: 4,
    rank: "major",
    people: "Violations of fundamental rights, privacy, autonomy, employment and livelihood.",
    organisation: "Operational losses, major data leaks, major legal infractions, dysfunctional governance.",
    ecosystem: "Concentration of power, systemic injustice, governance failure.",
  },
  {
    n: 3,
    rank: "moderate",
    people: "Social exclusion, reputational damage, misinformation, moderate mental health impacts.",
    organisation: "Reduced margins, reputational harm, customer churn, diminished brand value.",
    ecosystem: "Erosion of diversity, fragmented collaboration, restricted social mobility.",
  },
  {
    n: 2,
    rank: "minor",
    people: "Setbacks in career progress, diminished savings, reduced flexibility, self-doubt.",
    organisation: "Low morale, moderate staff turnover, suboptimal processes.",
    ecosystem: "Slowdown of innovation, erosion of mutual trust, difficulty scaling.",
  },
  {
    n: 1,
    rank: "negligible",
    people: "Mild existential discontent, uncertainty about future direction.",
    organisation: "Gradual erosion of brand heritage, incomplete mission achievement.",
    ecosystem: "Slow infrastructure upgrades, missed cross-ecosystem synergies.",
  },
];

const renderHarmRankScaleSection = () => (
  <div className="harm-ladder-block">
    <div className="harm-ladder-head">
      <span className="harm-ladder-head-title">Understanding the harm ranks</span>
      <span className="harm-ladder-head-hint">Each harm is ranked 1 to 5 across People, Organisation, and Ecosystem.</span>
    </div>
    <div className="harm-ladder">
      <div className="harm-ladder-row harm-ladder-row--header">
        <div className="harm-ladder-cell harm-ladder-cell--rank">Rank</div>
        <div className="harm-ladder-cell">People</div>
        <div className="harm-ladder-cell">Organisation</div>
        <div className="harm-ladder-cell">Ecosystem</div>
      </div>
      {HARM_LADDER.map((row) => {
        const color = STAKEHOLDER_RANK_PILL_STYLE[row.rank]?.fg;
        return (
          <div key={row.n} className="harm-ladder-row">
            <div className="harm-ladder-cell harm-ladder-cell--rank" style={{ borderLeftColor: color }}>
              <span
                className="harm-ladder-chip"
                style={{ backgroundColor: color, color: STAKEHOLDER_RANK_FILL_TEXT[row.rank] }}
              >
                {row.n}
              </span>
              <span className="harm-ladder-rank-label">{STAKEHOLDER_RANK_TITLE[row.rank]}</span>
            </div>
            <div className="harm-ladder-cell">{row.people}</div>
            <div className="harm-ladder-cell">{row.organisation}</div>
            <div className="harm-ladder-cell">{row.ecosystem}</div>
          </div>
        );
      })}
    </div>
  </div>
);

type AssessmentOutcomeKey = "no_risk_assessment_required" | "risk_assessment_required";

const getAssessmentOutcomeKey = (reportData: ImpactPlusReport): AssessmentOutcomeKey => {
  const { cohortRankings } = reportData.decisionGuidance;
  const criticalCount = cohortRankings.find((r) => r.riskRankLabel === "critical")?.numCohorts ?? 0;
  const highCount = cohortRankings.find((r) => r.riskRankLabel === "high")?.numCohorts ?? 0;

  if (criticalCount > 0 || highCount > 0) return "risk_assessment_required";

  if (reportData.decisionGuidance.verdictLevel >= 3) return "risk_assessment_required";
  return "no_risk_assessment_required";
};

const val = (v: string | undefined) => (v && v.trim() && v.trim() !== "N/A" ? v.trim() : "");

const splitClauseList = (text: string): string[] =>
  text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const cleanBulletItem = (raw: string, stripLeadSubject: boolean): string => {
  let s = raw.replace(/^and\s+/i, "");
  if (stripLeadSubject) s = s.replace(/^.*?\bshould\s+/i, "");
  s = s.trim();
  if (!s) return s;
  s = s.charAt(0).toUpperCase() + s.slice(1);
  if (!/[.?!]$/.test(s)) s += ".";
  return s;
};

const parseControllingDomainBullet = (bullet: string): { title: string; items: string[] } => {
  const colonIdx = bullet.indexOf(":");
  if (colonIdx === -1) return { title: "Safety is the controlling domain", items: [bullet] };
  const title = bullet.slice(0, colonIdx).trim();
  const rest = bullet.slice(colonIdx + 1).trim();
  const items = splitClauseList(rest).map((p, i) => cleanBulletItem(p, i === 0));
  return { title, items };
};

const parseEvidenceGapsBullet = (bullet: string): { items: string[]; footnote: string } => {
  const semiIdx = bullet.indexOf(";");
  const mainPart = semiIdx === -1 ? bullet : bullet.slice(0, semiIdx);
  const footPart = semiIdx === -1 ? "" : bullet.slice(semiIdx + 1).trim();

  const includingIdx = mainPart.toLowerCase().indexOf("including ");
  const itemsText = includingIdx === -1 ? mainPart : mainPart.slice(includingIdx + "including ".length);
  const items = splitClauseList(itemsText).map((p) => cleanBulletItem(p, false));

  let footnote = footPart.replace(/^these should be\s+/i, "").trim();
  if (footnote) {
    footnote = footnote.charAt(0).toUpperCase() + footnote.slice(1);
    if (!/[.?!]$/.test(footnote)) footnote += ".";
  }
  return { items, footnote };
};

const renderDeploymentVerdictSection = (reportData: ImpactPlusReport) => {
  const { decisionGuidance, impactedStakeholders } = reportData;

  const getRankingCount = (label: string) =>
    decisionGuidance.cohortRankings.find((r) => r.riskRankLabel === label)?.numCohorts ?? 0;

  const criticalCount = getRankingCount("critical");
  const majorCount = getRankingCount("high");
  const moderateCount = getRankingCount("moderate");
  const totalAssessed =
    decisionGuidance.cohortRankings.reduce((sum, r) => sum + (r.numCohorts ?? 0), 0) ||
    (impactedStakeholders?.impactedCohorts ?? []).length;

  const allRows: StakeholderImpactRow[] = (impactedStakeholders?.impactedCohorts ?? [])
    .map(cohortToStakeholderRow)
    .sort((a, b) => HARM_RANK_ORDER[a.harmRank] - HARM_RANK_ORDER[b.harmRank]);

  const detailedRankCounts = allRows.reduce<Record<string, number>>((acc, row) => {
    acc[row.harmRank] = (acc[row.harmRank] ?? 0) + 1;
    return acc;
  }, {});
  const remainderTiles = decisionGuidance.cohortRankings
    .map((r) => {
      const rank = cohortRiskToHarmRank(r.riskRankLabel);
      return { rank, surplus: (r.numCohorts ?? 0) - (detailedRankCounts[rank] ?? 0) };
    })
    .filter((t) => t.surplus > 0)
    .sort((a, b) => HARM_RANK_ORDER[a.rank] - HARM_RANK_ORDER[b.rank]);

  const COHORT_TILE_LIMIT = 6;
  const isTopSeverity = (rank: StakeholderHarmRank) => rank !== "minor" && rank !== "negligible";
  type CohortTile =
    | { kind: "row"; key: string; row: StakeholderImpactRow }
    | { kind: "remainder"; key: string; tile: { rank: StakeholderHarmRank; surplus: number } };
  const topSeverityTiles: CohortTile[] = [
    ...allRows
      .filter((row) => isTopSeverity(row.harmRank))
      .map((row, idx): CohortTile => ({ kind: "row", key: `${row.stakeholderGroup}-${idx}`, row })),
    ...remainderTiles
      .filter((tile) => isTopSeverity(tile.rank))
      .map((tile): CohortTile => ({ kind: "remainder", key: `remainder-${tile.rank}`, tile })),
  ];
  const cohortTiles = topSeverityTiles.slice(0, COHORT_TILE_LIMIT);
  const topSeverityCohortCount =
    allRows.filter((row) => isTopSeverity(row.harmRank)).length +
    remainderTiles.filter((tile) => isTopSeverity(tile.rank)).reduce((sum, tile) => sum + tile.surplus, 0);

  const [controllingScenarioBullet, controllingDomainBullet, evidenceGapsBullet] =
    decisionGuidance.executiveBullets ?? [];

  const controllingSeverityRank = allRows[0]?.harmRank;
  const controllingSeverityLabel = controllingSeverityRank
    ? (STAKEHOLDER_RANK_CARD_LABEL[controllingSeverityRank] ?? controllingSeverityRank.toUpperCase())
    : "";
  const controllingScenarioColor = controllingSeverityRank
    ? STAKEHOLDER_RANK_PILL_STYLE[controllingSeverityRank].fg
    : STAKEHOLDER_RANK_PILL_STYLE.critical.fg;
  const controllingScenarioTextColor =
    controllingSeverityRank === "critical" || !controllingSeverityRank
      ? "var(--aq-critical-on-dark)"
      : controllingScenarioColor;

  const { title: safetyDomainTitle, items: safetyDomainItems } = controllingDomainBullet
    ? parseControllingDomainBullet(controllingDomainBullet)
    : { title: "", items: [] };

  const { items: evidenceGapItems, footnote: evidenceFootnote } = evidenceGapsBullet
    ? parseEvidenceGapsBullet(evidenceGapsBullet)
    : { items: [], footnote: "" };

  const stats: { count: number; label: string; rank: StakeholderHarmRank }[] = [
    { count: criticalCount, label: "Critical cohorts", rank: "critical" },
    { count: majorCount, label: "Major cohorts", rank: "major" },
    { count: moderateCount, label: "Moderate cohorts", rank: "moderate" },
  ];

  return (
    <section className="container report-page deployment-verdict-page" id="toc-deployment-verdict">
      <div className="deployment-verdict-eyebrow">01&nbsp;&nbsp;DEPLOYMENT VERDICT</div>

      <h1 className="deployment-verdict-title">
        <span className="deployment-verdict-icon" aria-hidden="true">
          <VerdictIconGppMaybe />
        </span>
        {decisionGuidance.verdictLabel || "Assessment Complete"}
      </h1>
      {decisionGuidance.verdict && (
        <p className="deployment-verdict-subtitle">{decisionGuidance.verdict}</p>
      )}

      <div className="deployment-verdict-stats">
        {stats.map((s) => (
          <div key={s.rank} className={`deployment-verdict-stat deployment-verdict-stat--${s.rank}`}>
            <div className="deployment-verdict-stat-top">
              <span
                className="deployment-verdict-stat-dot"
                aria-hidden="true"
                style={{ backgroundColor: STAKEHOLDER_RANK_PILL_STYLE[s.rank].fg }}
              />
              <span className="deployment-verdict-stat-count">{s.count}</span>
            </div>
            <span className="deployment-verdict-stat-label">{s.label}</span>
          </div>
        ))}
        <div className="deployment-verdict-stat deployment-verdict-stat--total">
          <div className="deployment-verdict-stat-top">
            <span className="deployment-verdict-stat-count">{totalAssessed}</span>
          </div>
          <span className="deployment-verdict-stat-label">Cohorts assessed</span>
        </div>
      </div>

      {cohortTiles.length > 0 && (
        <div className="deployment-verdict-stakeholders">
          <h4 className="deployment-verdict-stakeholders-title">Impacted Stakeholders by Harm Rank</h4>
          <p className="deployment-verdict-stakeholders-subtitle">
            {topSeverityCohortCount > cohortTiles.length
              ? `Top ${cohortTiles.length} of ${topSeverityCohortCount} assessed cohorts, ordered from most to least severely affected.`
              : `The ${topSeverityCohortCount} assessed cohorts, ordered from most to least severely affected.`}
          </p>
          <div className="deployment-verdict-cohort-grid">
            {cohortTiles.map((tile) => {
              if (tile.kind === "row") {
                const pillStyle = STAKEHOLDER_RANK_PILL_STYLE[tile.row.harmRank];
                return (
                  <div key={tile.key} className="deployment-verdict-cohort-card" style={{ borderTopColor: pillStyle.fg }}>
                    <span className="deployment-verdict-cohort-head">
                      <span className="deployment-verdict-cohort-rank" style={{ color: pillStyle.fg }}>
                        {STAKEHOLDER_RANK_CARD_LABEL[tile.row.harmRank] ?? tile.row.harmRank.toUpperCase()}
                      </span>
                    </span>
                    <span className="deployment-verdict-cohort-title">{tile.row.stakeholderGroup}</span>
                  </div>
                );
              }
              const pillStyle = STAKEHOLDER_RANK_PILL_STYLE[tile.tile.rank];
              return (
                <div key={tile.key} className="deployment-verdict-cohort-card" style={{ borderTopColor: pillStyle.fg }}>
                  <span className="deployment-verdict-cohort-head">
                    <span className="deployment-verdict-cohort-icon" aria-hidden="true"><MIcon name="groups" size={18} /></span>
                    <span className="deployment-verdict-cohort-rank" style={{ color: pillStyle.fg }}>
                      {STAKEHOLDER_RANK_CARD_LABEL[tile.tile.rank] ?? tile.tile.rank.toUpperCase()}
                    </span>
                  </span>
                  <span className="deployment-verdict-cohort-title">
                    {tile.tile.surplus} further affected cohort{tile.tile.surplus !== 1 ? "s" : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {controllingScenarioBullet && (
        <div
          className="deployment-verdict-callout"
          style={{
            "--controlling-scenario-color": controllingScenarioColor,
            "--controlling-scenario-text-color": controllingScenarioTextColor,
          } as CSSProperties}
        >
          <div className="deployment-verdict-callout-header">
            <span className="deployment-verdict-callout-icon" aria-hidden="true"><WarningTriangleIcon /></span>
            Controlling Scenario{controllingSeverityLabel ? `, ${controllingSeverityLabel}` : ""}
          </div>
          <p className="deployment-verdict-callout-text">{controllingScenarioBullet}</p>
        </div>
      )}

      {(safetyDomainItems.length > 0 || evidenceGapItems.length > 0) && (
        <div className="deployment-verdict-info-grid">
          {safetyDomainItems.length > 0 && (
            <div className="deployment-verdict-info-box">
              <h5 className="deployment-verdict-info-box-title">
                <span className="deployment-verdict-info-box-icon" aria-hidden="true"><ShieldOutlineIcon /></span>
                {safetyDomainTitle}
              </h5>
              <ul className="deployment-verdict-info-box-list">
                {safetyDomainItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {evidenceGapItems.length > 0 && (
            <div className="deployment-verdict-info-box">
              <h5 className="deployment-verdict-info-box-title">
                <span className="deployment-verdict-info-box-icon" aria-hidden="true"><ChecklistIcon /></span>
                Evidence gaps driving the verdict
              </h5>
              <ul className="deployment-verdict-info-box-list">
                {evidenceGapItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              {evidenceFootnote && (
                <div className="deployment-verdict-info-box-footnote">{evidenceFootnote}</div>
              )}
            </div>
          )}
        </div>
      )}

      <ReportFooterMainPage />
    </section>
  );
};

const titleCaseFirst = (s: string): string => {
  const t = (s ?? "").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
};

const splitProseList = (text: string): string[] =>
  (text ?? "")
    .replace(/\s+and\s+/gi, ",")
    .replace(/&/g, ",")
    .split(",")
    .map((s) => s.replace(/\brespectively\b/gi, "").trim())
    .filter(Boolean);

const parseMarketsAndLanguages = (
  deploymentRegions: string,
): { markets: string[]; languages: string[] } => {
  const sentences = (deploymentRegions ?? "")
    .split(/\.\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const looksLikePlaceOrLang = (s: string) => /^[A-Z]/.test(s) && s.split(" ").length <= 3;

  const markets = sentences[0]
    ? splitProseList(sentences[0]).filter(looksLikePlaceOrLang)
    : [];

  const langSentence = sentences.find((s) => /support|language/i.test(s));
  const languages = langSentence
    ? splitProseList(langSentence.replace(/^.*?(supports?|languages?)\b[:\s]*/i, "")).filter(
      looksLikePlaceOrLang,
    )
    : [];

  return { markets, languages };
};

const renderAiUseCaseRegisterSection = (reportData: ImpactPlusReport, useCase?: UseCase) => {
  const uc = useCase;
  const useCaseTitle = val(uc?.useCaseSummary.useCaseTitle) || val(reportData.titlePage.useCaseTitle) || "This AI system";
  const summary = val(uc?.useCaseSummary.useCaseSummary);

  const { markets, languages } = parseMarketsAndLanguages(uc?.businessContext.deploymentRegions ?? "");

  const owner = val(uc?.useCaseSummary.useCaseOwner);
  const stats: { label: string; value: string; icon: string }[] = [
    { label: "Adoption Stage", icon: "rocket_launch", value: titleCaseFirst(val(uc?.useCaseSummary.adoptionStage)) || FALLBACK_CELL_VALUE },
    { label: "Business Criticality", icon: "flag", value: titleCaseFirst(val(uc?.businessContext.businessCriticality)) || FALLBACK_CELL_VALUE },
    { label: "Department", icon: "apartment", value: titleCaseFirst(val(uc?.useCaseSummary.department)) || FALLBACK_CELL_VALUE },
    { label: "Owner", icon: "person", value: owner || "Not assigned" },
    { label: "Regions", icon: "public", value: markets.length ? `${markets.length} market${markets.length !== 1 ? "s" : ""}` : FALLBACK_CELL_VALUE },
  ];

  return (
    <section className="container report-page usecase-register-page" id="toc-usecase-register">
      <div className="usecase-register-eyebrow">02&nbsp;&nbsp;AI USE CASE REGISTER</div>
      <h1 className="usecase-register-title">{useCaseTitle} deployment context</h1>
      {summary && <p className="usecase-register-summary">{summary}</p>}

      <div className="usecase-register-stat-grid">
        {stats.map((s) => (
          <div key={s.label} className="usecase-register-stat">
            <span className="usecase-register-stat-head">
              <span className="usecase-register-stat-icon" aria-hidden="true"><MIcon name={s.icon} size={17} /></span>
              <span className="usecase-register-stat-label">{s.label}</span>
            </span>
            <span className="usecase-register-stat-value" title={s.label === "Regions" ? markets.join(", ") : s.label === "Languages" ? languages.join(", ") : undefined}>
              {s.value}
            </span>
          </div>
        ))}
      </div>

      <ReportFooter />
    </section>
  );
};

const extractResidencyNote = (deploymentRegions: string): string => {
  const sentence = (deploymentRegions ?? "")
    .split(/\.\s+/)
    .map((s) => s.trim())
    .find((s) => /residency|region[- ]specific|data residence/i.test(s));
  if (!sentence) return "";
  return /[.?!]$/.test(sentence) ? sentence : `${sentence}.`;
};

const renderAiUseCaseRegisterDetailSection = (useCase?: UseCase) => {
  const uc = useCase;

  const usersRows = [
    { label: "Intended users", value: val(uc?.intendedUse.intendedUsers) },
    { label: "Accessible by", value: val(uc?.intendedUse.accessibleBy) },
    { label: "Use case scenario", value: val(uc?.intendedUse.useCaseScenarios) },
    { label: "Impacted nationalities", value: val(uc?.businessContext.impactedNationalities) },
  ].filter((r) => r.value);

  const dataRows = [
    { label: "Data context", value: val(uc?.dataContext.input.dataContentDescription) },
    { label: "Deployment environment", value: val(uc?.systemComponents.deploymentEnvironment) },
  ].filter((r) => r.value);

  const { markets, languages } = parseMarketsAndLanguages(uc?.businessContext.deploymentRegions ?? "");
  const regionPairs = markets.map((market, i) => ({ market, language: languages[i] ?? "" }));
  const residencyNote = extractResidencyNote(uc?.businessContext.deploymentRegions ?? "");

  const renderRows = (rows: { label: string; value: string }[]) =>
    rows.map((row) => (
      <div key={row.label} className="usecase-detail-row">
        <span className="usecase-detail-row-label">{row.label}</span>
        <span className="usecase-detail-row-value">{row.value}</span>
      </div>
    ));

  return (
    <section className="container report-page usecase-detail-page" id="toc-usecase-register-detail">
      <div className="usecase-register-eyebrow">02&nbsp;&nbsp;AI USE CASE REGISTER</div>
      <h1 className="usecase-register-title">Intended users, access, and hosting</h1>

      {usersRows.length > 0 && (
        <div className="usecase-detail-group">
          <div className="usecase-detail-group-head">
            <span className="usecase-detail-group-icon" aria-hidden="true"><GroupIcon /></span>
            <span className="usecase-detail-group-title">Users and access</span>
          </div>
          {renderRows(usersRows)}
        </div>
      )}

      {dataRows.length > 0 && (
        <div className="usecase-detail-group">
          <div className="usecase-detail-group-head">
            <span className="usecase-detail-group-icon" aria-hidden="true"><StorageIcon /></span>
            <span className="usecase-detail-group-title">Data and deployment</span>
          </div>
          {renderRows(dataRows)}
        </div>
      )}

      {regionPairs.length > 0 && (
        <div className="usecase-detail-regions">
          <div className="usecase-detail-regions-head">
            <span className="usecase-detail-regions-icon" aria-hidden="true"><GlobeIcon /></span>
            <span className="usecase-detail-regions-title">Regions of Deployment and Supported Languages</span>
          </div>
          <div className="usecase-detail-regions-grid">
            {regionPairs.map((pair, idx) => (
              <div key={idx} className="usecase-detail-region">
                <span className="usecase-detail-region-name">{pair.market}</span>
                {pair.language && <span className="usecase-detail-region-lang">{pair.language}</span>}
              </div>
            ))}
          </div>
          {residencyNote && <p className="usecase-detail-regions-note">{residencyNote}</p>}
        </div>
      )}

      <ReportFooter />
    </section>
  );
};

type MaintenanceCard = { title: string; icon: () => JSX.Element; sentences: string[] };

const MAINTENANCE_THEMES: { title: string; icon: () => JSX.Element; pattern: RegExp }[] = [
  { title: "Third-party model", icon: () => <MIcon name="cloud" size={17} />, pattern: /third[- ]party|managed|vendor|pre[- ]trained|model (update|monitor|version)/i },
  { title: "Testing and validation", icon: () => <MIcon name="science" size={17} />, pattern: /regression|test|validation|scenario|staging/i },
  { title: "Change control", icon: () => <MIcon name="rule" size={17} />, pattern: /change control|approval|governance|version control|audit|rule change/i },
  { title: "Ongoing review", icon: () => <MIcon name="monitoring" size={17} />, pattern: /review|monthly|annual|monitor|log/i },
];

const splitSentences = (text: string): string[] =>
  (text ?? "")
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

const parseMaintenanceCards = (maintenanceApproach: string): MaintenanceCard[] => {
  const sentences = splitSentences(maintenanceApproach);
  if (sentences.length < 2) return [];

  const cards: MaintenanceCard[] = MAINTENANCE_THEMES.map((t) => ({ title: t.title, icon: t.icon, sentences: [] }));
  for (const sentence of sentences) {
    const idx = MAINTENANCE_THEMES.findIndex((t) => t.pattern.test(sentence));
    if (idx >= 0) cards[idx].sentences.push(sentence);
  }
  const filled = cards.filter((c) => c.sentences.length > 0);
  return filled.length >= 2 ? filled : [];
};

const renderOversightSection = (useCase?: UseCase) => {
  const uc = useCase;
  const humanInTheLoop = val(uc?.businessContext.humanInTheLoop);
  const maintenanceApproach = val(uc?.businessContext.maintenanceApproach);
  const applicableSectors = val(uc?.businessContext.applicableSectors);

  const maintenanceCards = parseMaintenanceCards(maintenanceApproach);

  return (
    <section className="container report-page usecase-oversight-page" id="toc-usecase-oversight">
      <div className="usecase-register-eyebrow">02&nbsp;&nbsp;AI USE CASE REGISTER</div>
      <h1 className="usecase-register-title">Oversight and change control</h1>

      <div className="usecase-oversight-callout">
        <div className="usecase-oversight-callout-head">
          <span className="usecase-oversight-icon" aria-hidden="true"><MIcon name="supervisor_account" size={19} /></span>
          <span className="usecase-oversight-callout-title">Human in the loop</span>
        </div>
        <p className="usecase-oversight-callout-text">{humanInTheLoop || "No data available."}</p>
      </div>

      {maintenanceCards.length > 0 ? (
        <>
          <div className="usecase-oversight-group-head">
            <span className="usecase-oversight-icon" aria-hidden="true"><MIcon name="verified_user" size={19} /></span>
            <span className="usecase-oversight-group-title">Maintenance and change control</span>
          </div>
          <div className="usecase-oversight-maintenance-grid">
            {maintenanceCards.map((card) => {
              const CardIcon = card.icon;
              return (
                <div key={card.title} className="usecase-oversight-maintenance-card">
                  <div className="usecase-oversight-maintenance-card-head">
                    <span className="usecase-oversight-icon usecase-oversight-icon--card" aria-hidden="true"><CardIcon /></span>
                    <span className="usecase-oversight-maintenance-card-title">{card.title}</span>
                  </div>
                  <p className="usecase-oversight-maintenance-card-text">{card.sentences.join(" ")}</p>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        maintenanceApproach && (
          <>
            <div className="usecase-oversight-callout-head">
              <span className="usecase-oversight-icon" aria-hidden="true"><ShieldOutlineIcon /></span>
              <span className="usecase-oversight-callout-title">Maintenance and change control</span>
            </div>
            <div className="usecase-oversight-callout">
              <p className="usecase-oversight-callout-text">{maintenanceApproach}</p>
            </div>
          </>
        )
      )}

      {applicableSectors && (
        <div className="usecase-oversight-sectors">
          <div className="usecase-oversight-sectors-head">
            <span className="usecase-oversight-icon" aria-hidden="true"><DomainIcon /></span>
            <span className="usecase-oversight-sectors-title">Applicable sectors and obligations</span>
          </div>
          <p className="usecase-oversight-sectors-text">{applicableSectors}</p>
        </div>
      )}

      <ReportFooter />
    </section>
  );
};

const NEXT_STEPS_TITLE_BY_VERDICT: { minLevel: number; title: string }[] = [
  { minLevel: 4, title: "Inherent risk is unacceptable without mitigation" },
  { minLevel: 3, title: "Inherent risk requires structured mitigation" },
  { minLevel: 2, title: "Inherent risk is limited and manageable" },
  { minLevel: 0, title: "Inherent risk is negligible" },
];

const NEXT_STEPS_PROCEED_PATH: { title: string; description: string; icon: () => JSX.Element }[] = [
  { title: "Detailed risk assessment", description: "Profile each finding against standards and regulation.", icon: ChecklistIcon },
  { title: "Risk mitigation", description: "Design and implement controls for prioritised harms.", icon: ShieldOutlineIcon },
  { title: "Evidence and validation", description: "Representative testing, shadow runs, adverse event linkage.", icon: () => <MIcon name="verified" size={17} /> },
  { title: "Production monitoring", description: "Cohort-level and subgroup monitoring after deployment.", icon: MonitoringIcon },
];

const renderNextStepsSection = (reportData: ImpactPlusReport) => {
  const { nextSteps, decisionGuidance } = reportData;
  const outcomeKey = getAssessmentOutcomeKey(reportData);

  const title =
    NEXT_STEPS_TITLE_BY_VERDICT.find((t) => (decisionGuidance.verdictLevel ?? 0) >= t.minLevel)?.title ?? "Next steps";

  const distribution = [...decisionGuidance.cohortRankings]
    .sort((a, b) => (b.riskRankNum ?? 0) - (a.riskRankNum ?? 0))
    .filter((r) => (r.numCohorts ?? 0) > 0)
    .map((r) => {
      const rank = cohortRiskToHarmRank(r.riskRankLabel);
      return {
        rank,
        count: r.numCohorts ?? 0,
        label: STAKEHOLDER_RANK_TITLE[rank],
        color: STAKEHOLDER_RANK_PILL_STYLE[rank]?.fg,
        textOn: STAKEHOLDER_RANK_FILL_TEXT[rank],
      };
    });

  const introText = nextSteps.introduction || "";
  const calloutText = nextSteps.recommendedNextStep || "";

  return (
    <section className="container report-page next-steps-page" id="toc-next-steps">
      <div className="usecase-register-eyebrow">04&nbsp;&nbsp;NEXT STEPS</div>
      <h1 className="usecase-register-title">{title}</h1>

      {distribution.length > 0 && (
        <div className="next-steps-distribution">
          <div className="next-steps-distribution-bar">
            {distribution.map((d) => (
              <div
                key={d.rank}
                className="next-steps-distribution-seg"
                style={{ flexGrow: d.count, backgroundColor: d.color, color: d.textOn }}
              >
                {d.count}
              </div>
            ))}
          </div>
          <div className="next-steps-distribution-legend">
            {distribution.map((d) => (
              <span key={d.rank} className="next-steps-distribution-legend-item">
                <span
                  className="next-steps-distribution-legend-dot"
                  aria-hidden="true"
                  style={{ backgroundColor: d.color }}
                />
                {d.count} {d.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {introText && <p className="next-steps-intro">{introText}</p>}

      {outcomeKey === "no_risk_assessment_required" && (
        <p className="next-steps-intro">
          The identified Moderate and Minor impacts are manageable through standard operational practice and do not require
          a formal risk assessment prior to deployment. Deployers are nonetheless encouraged to document the findings of this
          assessment as part of their AI governance record and to establish a monitoring cadence appropriate to the scale and
          context of deployment.
        </p>
      )}

      {calloutText && (
        <div className="next-steps-dark-callout">
          <span className="next-steps-dark-callout-icon" aria-hidden="true"><ArrowCircleRightIcon /></span>
          <div>
            <div className="next-steps-dark-callout-eyebrow">Required next step</div>
            <p className="next-steps-dark-callout-text">{calloutText}</p>
          </div>
        </div>
      )}

      <div className="next-steps-path-grid">
        {NEXT_STEPS_PROCEED_PATH.map((step) => {
          const StepIcon = step.icon;
          return (
            <div key={step.title} className="next-steps-path-card">
              <span className="next-steps-path-card-icon" aria-hidden="true"><StepIcon /></span>
              <div className="next-steps-path-card-title">{step.title}</div>
              <div className="next-steps-path-card-desc">{step.description}</div>
            </div>
          );
        })}
      </div>

      <ReportFooter />
    </section>
  );
};

const READING_SEQUENCE: { n: string; title: string; description: string }[] = [
  {
    n: "01",
    title: "Deployment verdict",
    description: "The one-line recommendation and the rank summary of the highest-exposure cohorts.",
  },
  {
    n: "02",
    title: "AI Use Case Register",
    description: "Deployment context, intended users, accountable roles, data handling, and operational characteristics.",
  },
  {
    n: "03",
    title: "Stakeholder impact",
    description: "Cohorts affected and foreseeable harms, ranked by severity. Each card can be assigned to an owner.",
  },
  {
    n: "04",
    title: "Next steps",
    description: "Overall exposure picture and the formal risk management process required before deployment.",
  },
];

const VERDICT_REFERENCE: { rank: StakeholderHarmRank | "na"; label: string; meaning: string }[] = [
  {
    rank: "critical",
    label: "Critical",
    meaning:
      "One or more Critical (Rank 5) impacts identified. Consequences would be severe and potentially system-threatening for the organisation.",
  },
  {
    rank: "major",
    label: "Major",
    meaning:
      "Major (Rank 4) impacts identified across one or more stakeholder groups. Significant institutional impact requiring priority attention before deployment.",
  },
  {
    rank: "moderate",
    label: "Moderate",
    meaning:
      "Moderate (Rank 3) impacts identified. Meaningful exposure requiring structured mitigation during or shortly after deployment.",
  },
  {
    rank: "minor",
    label: "Minor",
    meaning:
      "Only Minor (Rank 2) impacts identified. Limited exposure manageable through standard operational controls.",
  },
  {
    rank: "negligible",
    label: "Negligible",
    meaning:
      "Only Negligible (Rank 1) impacts identified. Exposure is present but consequences are minor and unlikely to require dedicated action.",
  },
  {
    rank: "na",
    label: "Not applicable",
    meaning: "The risk domain or scenario was assessed and determined not applicable to this deployment.",
  },
];

const renderHowToRead = (reportData: ImpactPlusReport) => {
  return (
    <>
      <section className="container report-page how-to-read-page" id="toc-how-to-read">
        <div className="usecase-register-eyebrow">05&nbsp;&nbsp;REFERENCE</div>
        <h1 className="usecase-register-title">How to read this report</h1>

        <p className="usecase-register-summary">
          This report provides a stakeholder impact snapshot for the deployment of{" "}
          <strong>{reportData.titlePage.useCaseTitle}</strong>. It identifies the cohorts affected, the harms each may
          foreseeably experience, and the rank of those harms. It does not assess whether mitigation requirements are
          implemented, nor validate existing controls. It is a planning tool to support informed deployment decisions and
          readiness for Full Assurance.
        </p>

        <div className="reading-seq-grid">
          {READING_SEQUENCE.map((step) => (
            <div key={step.n} className="reading-seq-card">
              <div className="reading-seq-card-num">{step.n}</div>
              <div className="reading-seq-card-title">{step.title}</div>
              <div className="reading-seq-card-desc">{step.description}</div>
            </div>
          ))}
        </div>

        {renderHarmRankScaleSection()}

        <ReportFooter />
      </section>

      <section className="container report-page impact-verdict-reference-page">
        <div className="usecase-register-eyebrow">05&nbsp;&nbsp;REFERENCE</div>
        <h1 className="usecase-register-title">Impact verdict reference</h1>

        <p className="usecase-register-summary">
          The overall impact verdict is determined automatically from the severity and number of harm ranks identified
          across all stakeholder groups. It reflects the scale of potential impact in this deployment context and indicates
          the level of risk assessment and mitigation required before deployment.
        </p>

        <div className="verdict-ref-table">
          {VERDICT_REFERENCE.map((row) => {
            const color = row.rank === "na" ? "var(--risk-na)" : STAKEHOLDER_RANK_PILL_STYLE[row.rank]?.fg;
            return (
              <div key={row.label} className="verdict-ref-row">
                <div className="verdict-ref-rank" style={{ borderLeftColor: color }}>
                  <span className="verdict-ref-dot" aria-hidden="true" style={{ backgroundColor: color }} />
                  <span className="verdict-ref-label">{row.label}</span>
                </div>
                <div className="verdict-ref-meaning">{row.meaning}</div>
              </div>
            );
          })}
        </div>

        <ReportFooter />
      </section>
    </>
  );
};

export const RenderImpactPlusReport = forwardRef<HTMLDivElement, { reportData: ImpactPlusReport; useCase?: UseCase }>(
  function RenderImpactPlusReport({ reportData, useCase }, ref): JSX.Element {
    useReportPagination("risk-report-content");
    useTocPageNumbers("risk-report-content");

    return (
      <>
        <style>{`:root { --print-copyright: "© ${new Date().getFullYear()} AIQURIS. Confidential and Proprietary."; }`}</style>
        <div id="risk-report-content" ref={ref}>
          <div className="report-content-inner">
            {renderTitlePage(reportData)}
            {renderDeploymentVerdictSection(reportData)}
            {renderAiUseCaseRegisterSection(reportData, useCase)}
            {renderAiUseCaseRegisterDetailSection(useCase)}
            {renderOversightSection(useCase)}
            {renderStakeholderMapSection(reportData)}
            {renderStakeholderCardsSections(reportData)}
            {renderNextStepsSection(reportData)}
            {renderHowToRead(reportData)}
          </div>
          <div className="report-footer-fixed" aria-hidden="true">
            <ReportFooter />
          </div>
        </div>
      </>
    );
  }
);
